import { useState } from "react";
import { usePlatformSummary, type PlatformCompanySummary } from "@/hooks/usePlatformSummary";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  LogOut, ArrowLeft, Building2, DollarSign,
  Users, CreditCard, Crown, Search, Settings, Image,
  TrendingUp, TrendingDown, AlertTriangle, BarChart3, UserCheck,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import commandxLogo from "@/assets/commandx-logo.png";
import { SEO } from "@/components/SEO";
import OnboardCompanyDialog from "@/components/platform/OnboardCompanyDialog";
import EditSubscriptionDialog from "@/components/platform/EditSubscriptionDialog";
import CompanyDetailDrawer from "@/components/platform/CompanyDetailDrawer";
import { MrrTrendPanel } from "@/components/platform/MrrTrendPanel";
import { useMrrTrend } from "@/hooks/useTenantBilling";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { ResponsiveContainer, AreaChart, Area } from "recharts";

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);

/* ── KPI Card ── */
const KpiCard = ({ label, value, icon: Icon, sub, badge }: {
  label: string; value: string; icon: React.ElementType; sub?: string;
  badge?: { text: string; variant: "default" | "destructive" | "secondary" };
}) => (
  <Card>
    <CardContent className="flex items-center gap-4 p-4">
      <div className="rounded-lg bg-primary/10 p-3">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-muted-foreground">{label}</p>
        <div className="flex items-center gap-2">
          <p className="text-xl font-bold">{value}</p>
          {badge && (
            <Badge variant={badge.variant} className="text-[10px] px-1.5 py-0">
              {badge.text}
            </Badge>
          )}
        </div>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

/* ── Badge colors ── */
const tierColor: Record<string, string> = {
  base: "bg-primary text-primary-foreground",
  starter: "bg-secondary text-secondary-foreground",
  pro: "bg-primary text-primary-foreground",
  enterprise: "bg-accent text-accent-foreground",
};
const statusColor: Record<string, string> = {
  active: "bg-primary text-primary-foreground",
  trial: "bg-secondary text-secondary-foreground",
  past_due: "bg-destructive text-destructive-foreground",
  suspended: "bg-destructive text-destructive-foreground",
  cancelled: "bg-muted text-muted-foreground",
};

/* ── Status filter options ── */
const STATUS_FILTERS = ["all", "active", "trial", "past_due", "suspended", "cancelled"] as const;

/* ── Company Card — Enhanced ── */
const CompanyCard = ({
  c, onClick, onManage, onImpersonate,
}: {
  c: PlatformCompanySummary;
  onClick: () => void;
  onManage: (e: React.MouseEvent) => void;
  onImpersonate: (e: React.MouseEvent) => void;
}) => {
  // Churn risk: no projects + trial status
  const isChurnRisk = c.project_count === 0 && c.subscription_status === "active";
  const trialExpiring = c.subscription_status === "trial" && c.trial_ends_at &&
    Math.ceil((new Date(c.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) < 5;
  const trialDaysLeft = c.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(c.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <Card className="cursor-pointer transition-shadow hover:shadow-md" onClick={onClick}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4 text-primary" />
            {c.company_name}
          </CardTitle>
          <div className="flex items-center gap-1.5">
            <Badge className={tierColor[c.plan_tier] ?? "bg-muted text-muted-foreground"}>
              {c.plan_tier === "none" ? "No Plan" : c.plan_tier}
            </Badge>
            <Badge className={statusColor[c.subscription_status] ?? "bg-muted text-muted-foreground"}>
              {c.subscription_status === "none" ? "—" : c.subscription_status.replace("_", " ")}
            </Badge>
            {isChurnRisk && (
              <Badge variant="destructive" className="text-[10px]">At Risk</Badge>
            )}
            {trialExpiring && (
              <Badge className="bg-amber-500 text-white text-[10px]">Trial Expiring</Badge>
            )}
            <Button
              variant="ghost" size="icon" className="h-7 w-7 ml-1"
              onClick={onManage}
              title="Manage subscription"
            >
              <Settings className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div><span className="text-muted-foreground">Projects</span><p className="font-semibold">{c.project_count}</p></div>
          <div><span className="text-muted-foreground">Users</span><p className="font-semibold">{c.active_users} / {c.seat_count}</p></div>
          <div><span className="text-muted-foreground">MRR</span><p className="font-semibold">{fmt(c.calculated_mrr)}</p></div>
        </div>

        {/* Trial days badge */}
        {c.subscription_status === "trial" && trialDaysLeft !== null && (
          <div className="text-xs text-muted-foreground">
            Trial: <span className="font-medium">{trialDaysLeft} days remaining</span>
          </div>
        )}

        {/* Login as tenant */}
        <div className="flex justify-end pt-1">
          <Button
            size="sm" variant="outline" className="h-7 text-xs"
            onClick={onImpersonate}
          >
            <UserCheck className="h-3 w-3 mr-1" />
            Login as tenant
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/* ── Dashboard ── */
const PlatformAdminDashboard = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { data: companies = [], isLoading, error } = usePlatformSummary();
  const { trendData, currentMrr } = useMrrTrend(30);
  const { startImpersonation } = useImpersonation();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [drawerCompany, setDrawerCompany] = useState<PlatformCompanySummary | null>(null);
  const [editCompany, setEditCompany] = useState<PlatformCompanySummary | null>(null);
  const [impersonateTarget, setImpersonateTarget] = useState<PlatformCompanySummary | null>(null);

  const filtered = companies.filter((c) => {
    if (statusFilter !== "all" && c.subscription_status !== statusFilter) return false;
    if (search && !c.company_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  /* PLATFORM CONTEXT totals */
  const totals = companies.reduce(
    (acc, c) => ({
      companies: acc.companies + 1,
      mrr: acc.mrr + c.calculated_mrr,
      projects: acc.projects + c.project_count,
      users: acc.users + c.active_users,
      seats: acc.seats + c.seat_count,
      active: acc.active + (c.subscription_status === "active" ? 1 : 0),
      trial: acc.trial + (c.subscription_status === "trial" ? 1 : 0),
    }),
    { companies: 0, mrr: 0, projects: 0, users: 0, seats: 0, active: 0, trial: 0 },
  );

  // Churn risk count
  const churnRiskCount = companies.filter(
    (c) => c.project_count === 0 && c.subscription_status === "active"
  ).length;

  // MRR change (approx from trend data)
  const mrrChange = trendData.length >= 2
    ? trendData[trendData.length - 1].mrr - trendData[0].mrr
    : 0;

  const payingCount = companies.filter(
    (c) => c.subscription_status === "active" && c.calculated_mrr > 0
  ).length;
  const avgRevenue = payingCount > 0 ? totals.mrr / payingCount : 0;

  const handleImpersonate = async () => {
    if (!impersonateTarget) return;
    await startImpersonation(impersonateTarget.company_id, impersonateTarget.company_name);
    setImpersonateTarget(null);
    navigate("/controller");
  };

  const initials = user?.email ? user.email.substring(0, 2).toUpperCase() : "PA";

  return (
    <div className="min-h-screen bg-background">
      <SEO title="Platform Admin" description="SaaS management console" />

      {/* Header */}
      <header className="border-b bg-card px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/controller")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold">Platform Admin</h1>
        </div>
        <div className="flex items-center gap-3">
          <OnboardCompanyDialog />
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="text-xs bg-primary text-primary-foreground">{initials}</AvatarFallback>
            </Avatar>
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium leading-none">{user?.email}</p>
              <div className="flex items-center gap-1 mt-0.5">
                <Crown className="h-3 w-3 text-primary" />
                <span className="text-xs text-muted-foreground">Platform Owner</span>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => signOut()}>
            <LogOut className="h-4 w-4 mr-1" /> Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {isLoading && (
          <div className="flex justify-center py-20">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        )}

        {error && (
          <Card>
            <CardContent className="p-6 text-destructive">
              Failed to load platform data. You may not have platform admin access.
            </CardContent>
          </Card>
        )}

        {!isLoading && !error && (
          <>
            {/* 8 KPI Cards */}
            <section>
              <h2 className="text-sm font-medium text-muted-foreground mb-3">Platform Overview</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KpiCard label="Total MRR" value={fmt(totals.mrr)} icon={CreditCard} />
                <KpiCard
                  label="MRR Change (30d)"
                  value={`${mrrChange >= 0 ? "+" : ""}${fmt(mrrChange)}`}
                  icon={mrrChange >= 0 ? TrendingUp : TrendingDown}
                  badge={mrrChange >= 0
                    ? { text: "▲", variant: "default" }
                    : { text: "▼", variant: "destructive" }
                  }
                />
                <KpiCard
                  label="Active Companies"
                  value={String(totals.active)}
                  icon={Building2}
                  sub={`${totals.projects} projects`}
                />
                <KpiCard
                  label="Trial Companies"
                  value={String(totals.trial)}
                  icon={Building2}
                />
                <KpiCard
                  label="Total Users"
                  value={`${totals.users} of ${totals.seats}`}
                  icon={Users}
                  sub="seats used"
                />
                <KpiCard
                  label="Churn Risk"
                  value={String(churnRiskCount)}
                  icon={AlertTriangle}
                  badge={churnRiskCount > 0
                    ? { text: String(churnRiskCount), variant: "destructive" }
                    : undefined
                  }
                />
                <KpiCard
                  label="ARR"
                  value={fmt(totals.mrr * 12)}
                  icon={BarChart3}
                />
                <KpiCard
                  label="Avg Revenue / Account"
                  value={fmt(avgRevenue)}
                  icon={DollarSign}
                  sub={`${payingCount} paying`}
                />
              </div>

              {/* MRR Sparkline */}
              {trendData.length > 1 && (
                <div className="mt-3 flex items-center gap-4">
                  <div className="h-10 flex-1 max-w-xs">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={trendData}>
                        <defs>
                          <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <Area
                          type="monotone"
                          dataKey="mrr"
                          stroke="hsl(var(--primary))"
                          strokeWidth={1.5}
                          fill="url(#sparkFill)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <span className="text-sm font-semibold text-primary">{fmt(currentMrr)}</span>
                </div>
              )}
            </section>

            {/* MRR Trend Panel */}
            <MrrTrendPanel />

            {/* Search + Filters */}
            <section className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search companies…"
                  className="pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {STATUS_FILTERS.map((s) => (
                  <Badge
                    key={s}
                    variant={statusFilter === s ? "default" : "outline"}
                    className="cursor-pointer capitalize"
                    onClick={() => setStatusFilter(s)}
                  >
                    {s === "all" ? "All" : s.replace("_", " ")}
                  </Badge>
                ))}
              </div>
            </section>

            {/* Company cards */}
            <section>
              <h2 className="text-sm font-medium text-muted-foreground mb-3">
                Companies {filtered.length !== companies.length && `(${filtered.length} of ${companies.length})`}
              </h2>
              {filtered.length === 0 ? (
                <Card>
                  <CardContent className="p-6 text-muted-foreground">
                    {companies.length === 0
                      ? 'No companies found. Use "Onboard Company" to add your first tenant.'
                      : "No companies match the current filters."}
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtered.map((c) => (
                    <CompanyCard
                      key={c.company_id}
                      c={c}
                      onClick={() => setDrawerCompany(c)}
                      onManage={(e) => { e.stopPropagation(); setEditCompany(c); }}
                      onImpersonate={(e) => { e.stopPropagation(); setImpersonateTarget(c); }}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Platform Branding */}
            <section>
              <h2 className="text-sm font-medium text-muted-foreground mb-3">Platform Branding</h2>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Image className="h-4 w-4 text-primary" />
                    CommandX Document Branding
                  </CardTitle>
                  <CardDescription>
                    Control how the CommandX platform logo appears on tenant-generated documents (invoices, estimates, bills, work orders, etc.)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-center gap-3">
                      <img src={commandxLogo} alt="CommandX" className="h-6 object-contain" />
                      <span className="text-sm font-medium">Current platform logo</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-medium">Show "Powered by CommandX" on documents</Label>
                      <p className="text-xs text-muted-foreground">Displays the CommandX logo and branding in the footer of all tenant PDFs and printable documents</p>
                    </div>
                    <Switch
                      defaultChecked={true}
                      onCheckedChange={(checked) => {
                        toast.success(checked ? "CommandX branding enabled on all tenant documents" : "CommandX branding hidden from tenant documents");
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-medium">Show "Powered by CommandX" on estimate approval page</Label>
                      <p className="text-xs text-muted-foreground">Displays CommandX branding on the public customer-facing estimate approval page</p>
                    </div>
                    <Switch
                      defaultChecked={true}
                      onCheckedChange={(checked) => {
                        toast.success(checked ? "Estimate page branding enabled" : "Estimate page branding hidden");
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </section>
          </>
        )}
      </main>

      {/* Drawer & Dialog */}
      <CompanyDetailDrawer
        company={drawerCompany}
        open={!!drawerCompany}
        onOpenChange={(o) => { if (!o) setDrawerCompany(null); }}
        onEditSubscription={() => {
          setEditCompany(drawerCompany);
        }}
      />
      <EditSubscriptionDialog
        company={editCompany}
        open={!!editCompany}
        onOpenChange={(o) => { if (!o) setEditCompany(null); }}
      />

      {/* Impersonation Confirmation Modal */}
      <Dialog open={!!impersonateTarget} onOpenChange={(o) => { if (!o) setImpersonateTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Admin Impersonation
            </DialogTitle>
            <DialogDescription>
              You are about to access <strong>{impersonateTarget?.company_name}</strong>'s workspace as an admin.
              This action will be logged in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpersonateTarget(null)}>Cancel</Button>
            <Button onClick={handleImpersonate}>
              Confirm &amp; Enter Workspace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformAdminDashboard;
